/**
 * Wiederkehrende Bausteine aus dem Entwurf.
 *
 * Im Entwurf standen diese Muster als wiederholte Inline-Stile in der Vorlage;
 * hier sind sie einmal benannt. Die Klassen liegen in `styles/tokens.css`.
 */

import { useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { de, type ArbzgWarning, type TerminalStatus } from '@zeit/domain';

export function Kicker({ children }: { children: ReactNode }) {
  return <div className="kicker">{children}</div>;
}

/**
 * Der Rahmen jeder Ansicht.
 *
 * Vorher baute sich jede Ansicht ihren eigenen Kopf: die eine mit `h6`, die
 * andere mit einem fett gesetzten `div`, die dritte mit gar keinem. Wo man
 * war, verriet nur die Beschriftung im Menü. Seitdem alle vier denselben Kopf
 * tragen — Überschrift links, Bedienelemente rechts, eine Linie darunter —
 * sieht man beim Umschalten, dass es dieselbe Anwendung ist.
 */
export function Page({
  title,
  kicker,
  actions,
  children,
}: {
  title: string;
  kicker?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <main className="page">
      <header className="page-head">
        <div style={{ minWidth: 0 }}>
          {kicker && <Kicker>{kicker}</Kicker>}
          <h1 className="page-title">{title}</h1>
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      {children}
    </main>
  );
}

/**
 * Ein umrandeter Abschnitt mit Überschriftenzeile.
 *
 * Ersetzt drei verschiedene Bauformen, die vorher dasselbe meinten: die
 * `pane`-Spalte, das nackte `div` mit `h6` darüber und das `article` mit
 * Inline-Rahmen in der Verwaltung.
 *
 * `flush` nimmt die Polsterung heraus — für Tabellen, die am Rahmen kleben
 * sollen.
 */
export function Card({
  title,
  aside,
  flush,
  children,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="card">
      {(title || aside) && (
        <header className="card-head">
          {title && <h2 className="card-title">{title}</h2>}
          {aside && <div className="card-aside">{aside}</div>}
        </header>
      )}
      <div className={`card-body${flush ? ' card-body-flush' : ''}`}>{children}</div>
    </section>
  );
}

/**
 * Beschriftete Zahl.
 *
 * Dieselbe Zahl stand vorher in vier Grössen und drei Schreibweisen in den
 * Ansichten — jedes Mal als Inline-Stil mit `fontFamily`, `fontWeight: 800`
 * und einer ausgedachten Schriftgrösse. Jetzt gibt es drei Stufen.
 */
export function Figure({
  label,
  value,
  size = 'md',
}: {
  label: ReactNode;
  value: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  return (
    <div className={`figure figure-${size}`}>
      <Kicker>{label}</Kicker>
      <div className="figure-value num">{value}</div>
    </div>
  );
}

export function Stat({
  label,
  value,
  small,
  muted,
}: {
  label: ReactNode;
  value: ReactNode;
  small?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="stat">
      <Kicker>{label}</Kicker>
      <div
        className={`stat-value${small ? ' stat-value-sm' : ''}`}
        style={muted ? { color: 'var(--color-neutral-700)' } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

const STATUS_CLASS: Record<TerminalStatus, string> = {
  off: '',
  on: ' status-on',
  break: ' status-break',
  done: ' status-done',
};

export function StatusBanner({ status, label }: { status: TerminalStatus; label: string }) {
  return (
    <div className={`status${STATUS_CLASS[status]}`} role="status">
      <span className="status-mark" aria-hidden />
      <span className="status-label">{label}</span>
    </div>
  );
}

export function Notice({ warning }: { warning: ArbzgWarning }) {
  return (
    <div className={`notice notice-${warning.severity}`}>
      <span className="notice-paragraph">{warning.paragraph}</span>
      <span>{warning.message}</span>
    </div>
  );
}

export function Notices({ warnings }: { warnings: ArbzgWarning[] }) {
  if (!warnings.length) return null;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {warnings.map((w) => (
        <Notice key={w.code} warning={w} />
      ))}
    </div>
  );
}

export function Toast({ text, tone }: { text: string; tone?: 'error' }) {
  return (
    <div className={`toast${tone === 'error' ? ' toast-error' : ''}`} role="status" aria-live="polite">
      {text}
    </div>
  );
}

/**
 * Modaler Dialog.
 *
 * Schliesst mit Escape, hält den Fokus im Dialog und gibt ihn beim Schliessen
 * dorthin zurück, woher er kam. Vorher wurde nur das erste Feld angesprungen:
 * mit der Tabulatortaste lief man aus dem Dialog heraus in die Tabelle
 * dahinter, und nach dem Schliessen stand der Fokus wieder ganz oben auf der
 * Seite — bei siebzehn „Korrigieren“-Knöpfen ein langer Rückweg.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  title,
  kicker,
  onClose,
  children,
}: {
  title: string;
  kicker?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;

      const items = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !ref.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog" ref={ref} role="dialog" aria-modal="true" aria-label={title}>
        {kicker && <Kicker>{kicker}</Kicker>}
        <h3 style={{ margin: '4px 0 18px', fontSize: 26 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  children,
  hint,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor} style={{ display: 'block', marginBottom: 6 }}>
        <span className="kicker">{label}</span>
      </label>
      {children}
      {hint && (
        <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/**
 * Passwortfeld mit Auge zum Aufdecken.
 *
 * Beim Festlegen eines neuen Passworts sieht man sonst nicht, ob man sich
 * vertippt hat. Der Knopf liegt im Feld und ist kein Submit-Knopf.
 */
export function PasswordInput(props: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="password-input">
      <input {...props} className="input" type={visible ? 'text' : 'password'} />
      <button
        type="button"
        className="password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? de.auth.hidePassword : de.auth.showPassword}
        aria-pressed={visible}
        title={visible ? de.auth.hidePassword : de.auth.showPassword}
      >
        <EyeIcon crossed={visible} />
      </button>
    </div>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {crossed && <line x1="3" y1="21" x2="21" y2="3" />}
    </svg>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div
      style={{
        padding: '10px 12px',
        background: 'var(--color-danger-100)',
        color: 'var(--color-danger-800)',
        fontSize: 13,
      }}
      role="alert"
    >
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
      {children}
    </p>
  );
}

/** Ladezustand, der nicht flackert: erst nach kurzer Verzögerung sichtbar. */
export function Loading() {
  return (
    <div
      className="kicker"
      style={{ padding: 28, animation: 'fadeIn .4s ease .3s both' }}
      aria-live="polite"
    >
      {de.app.name} wird geladen …
    </div>
  );
}
